import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { queryClient } from '@/lib/queryClient';
import { clearPlanCache } from '@/lib/planCache';
import { clearCheckoutAwaiting } from '@/lib/checkoutAwaiting';
import { flushPendingEditsBeforeSelectionChange } from '@/lib/pendingEditsGuard';
import { trackConversion, pushConversionPrerequisites } from '@/lib/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, userData?: { first_name?: string; last_name?: string }) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Pusht `user_properties_set` mit dem eigenen `profiles.is_internal`-Flag
 * (Team-/Agentur-/E2E-Konten), damit GTM/GA4 interne Konten aus den
 * Funnel-Reports filtern koennen — E2E-Traffic unterdrueckt `isE2eTestSession`
 * schon, manuelle Nutzung interner Konten war bisher unsichtbar. Ein Select pro
 * Sign-in, RLS-gescoped (der User sieht nur sein eigenes Profil).
 *
 * Analytics darf den Login-Flow nie brechen: Select-Fehler, fehlende Zeile und
 * Exceptions fallen STILL auf den Push ohne Flag zurueck (kein Throw, kein Log,
 * kein Sentry — ein fehlendes Funnel-Property ist kein Nutzerproblem und wuerde
 * bei jedem Netzwerk-Hickser nur Rauschen erzeugen). `undefined` heisst dann
 * "unbekannt", und analytics.ts laesst den Key `user_internal` weg.
 *
 * Seit Feld C meldet diese Funktion zusaetzlich `signup_complete`, und zwar
 * NACH dem Property-Push. Grund: GA4 wendet eine spaeter gesetzte
 * User-Property nicht rueckwirkend auf ein frueheres Ereignis an. Frueher lag
 * `signup_complete` synchron im onAuthStateChange-Callback und damit VOR
 * `user_properties_set`; das Ereignis trug also nie ein `user_internal`. Die
 * Tragweite ist fuer diesen einen Meilenstein klein (ein frisch angelegtes
 * Konto ist per Konstruktion noch nicht intern), die Reihenfolge ist aber
 * dieselbe, die `feed_connected` braucht, und zwei verschiedene Reihenfolgen
 * fuer dieselbe Frage waeren eine Fehlerquelle mehr.
 *
 * Zwei Stufen entscheiden ueber den Versand: die 10-Sekunden-Heuristik des
 * Aufrufers (frische Anmeldung gegen Wiederkehr) und danach ein
 * Compare-and-Set auf `profiles.conversion_signup_tracked_at`. Nur wer eine
 * Zeile zurueckbekommt, meldet.
 *
 * Seit dem 16.09.2026 liegt hier auch der Enhanced-Conversions-Push (gehashte
 * E-Mail, nur mit Marketing-Einwilligung), zwischen Property-Push und
 * signup_complete; die Regel steht in analytics.ts:pushConversionPrerequisites.
 */
async function pushUserPropertiesWithInternalFlag(
  userId: string,
  signup: { isNewSignup: boolean; method: string },
  email: string | undefined,
): Promise<void> {
  let isInternal: boolean | undefined;
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('is_internal')
      .eq('user_id', userId)
      .maybeSingle();
    if (!error) {
      // `is_internal` fehlt im generierten Supabase-Typ bis zum naechsten
      // Typegen (gleiche Lage wie auto_upload_* in useFeedExperiment.ts).
      const row = data as unknown as { is_internal?: boolean | null } | null;
      isInternal = row?.is_internal ?? undefined;
    }
  } catch {
    // Still schlucken — Fallback ohne Flag, Begruendung im Doc-Kommentar oben.
  }
  // Reihenfolge und Consent-Gate liegen in analytics.ts: user_properties_set,
  // dann (nur mit ad_user_data granted) set_user_data, dann die Bereitschaft
  // fuer feed_connected. signup_complete folgt erst hinter diesem await.
  await pushConversionPrerequisites(userId, isInternal, email);

  if (!signup.isNewSignup) return;
  try {
    const { data, error } = await supabase
      .from('profiles')
      // Cast nur fuer die Typpruefung, das gesendete JSON ist unveraendert:
      // die Spalte fehlt im generierten Supabase-Typ bis zum naechsten Typegen.
      .update({ conversion_signup_tracked_at: new Date().toISOString() } as never)
      .eq('user_id', userId)
      .is('conversion_signup_tracked_at', null)
      .select('user_id');
    if (error) return;
    if (!data || data.length === 0) return;
    trackConversion('signup_complete', { method: signup.method });
  } catch {
    // Still schlucken, gleiche Haltung wie oben.
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Track previous user ID to detect user switches
    let previousUserId: string | null = null;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const newUserId = session?.user?.id ?? null;

        // Detect user switch or sign-out: nuke all cached data
        if (previousUserId && previousUserId !== newUserId) {
          // Clear React Query cache completely — prevents cross-user data leaks
          queryClient.clear();
          // Drop the persisted plan/subscription snapshot so the next user never
          // sees the previous user's tier (see lib/planCache.ts).
          clearPlanCache();
          // Die Wartemarke eines Kaufs gehoert dem Nutzer, der gekauft hat.
          // Sonst meldete der Feed-Check nach einem Kontowechsel im selben Tab
          // den Kauf des naechsten Kontos (NA1 Task 4b).
          clearCheckoutAwaiting();
          // Clear all app-specific localStorage
          localStorage.removeItem('feed-optimizer-selection');
          localStorage.removeItem('feed-optimizer-onboarding-dismissed');
          localStorage.removeItem('feed-optimizer-onboarding-seen');
          localStorage.removeItem('feed-optimizer-onboarding-expanded');
          localStorage.removeItem('feed-optimizer-flow');
        }
        previousUserId = newUserId;

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Track auth events
        if (event === 'SIGNED_IN' && session?.user) {
          const provider = session.user.app_metadata?.provider || 'email';
          // Supabase has no separate SIGNED_UP event — distinguish a fresh
          // signup from a returning login by comparing created_at to
          // last_sign_in_at. 10s tolerance covers the OAuth round-trip.
          const created = new Date(session.user.created_at).getTime();
          const lastSignIn = session.user.last_sign_in_at
            ? new Date(session.user.last_sign_in_at).getTime()
            : created;
          const isNewSignup = Math.abs(lastSignIn - created) < 10_000;

          // Bewusst Fire-and-forget, NICHT awaited: gotrue-js awaitet die
          // onAuthStateChange-Callbacks innerhalb seines Auth-Locks (SIGNED_IN
          // feuert u.a. aus _recoverAndRefresh beim Seitenaufbau), ein await
          // auf einen Supabase-Call hier wuerde deadlocken (Supabase-Doku zu
          // onAuthStateChange). setSession/setUser/setLoading sind an dieser
          // Stelle bereits synchron passiert.
          //
          // Seit Feld C liegt auch `signup_complete` in dieser Funktion, hinter
          // dem Property-Push und einem Compare-and-Set. Das ist der zweite
          // Grund, warum hier nicht awaited werden darf: die Reihenfolge muss
          // INNERHALB der asynchronen Funktion hergestellt werden, nicht durch
          // ein await an dieser Stelle. Die 10-Sekunden-Heuristik bleibt die
          // erste Stufe und wird als Parameter durchgereicht.
          void pushUserPropertiesWithInternalFlag(
            session.user.id,
            { isNewSignup, method: provider },
            session.user.email ?? undefined,
          );
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      previousUserId = session?.user?.id ?? null;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, userData?: { first_name?: string; last_name?: string }) => {
    const redirectUrl = `${window.location.origin}/`;

    // Derive ISO 3166-1 alpha-2 country from navigator.language (e.g. "de-DE" → "DE",
    // "en-US" → "US", bare "de" → "DE"). Used in the Activity Bot onboarding ping.
    let countryCode: string | undefined;
    try {
      const locale = typeof navigator !== 'undefined' ? navigator.language : undefined;
      if (locale) {
        const parts = locale.split('-');
        const candidate = (parts[1] || parts[0] || '').toUpperCase();
        if (/^[A-Z]{2}$/.test(candidate)) countryCode = candidate;
      }
    } catch {
      // navigator may be unavailable in some environments; locale stays undefined.
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { ...userData, country_code: countryCode }
      }
    });
    return { error };
  };

  const signOut = async () => {
    // K2-Befund: Abmelden mit offener Werkbank-Handkorrektur verlor die
    // Korrektur garantiert — der Unmount-Auto-Save feuert erst NACH dem
    // Entwerten der Session und scheitert dann an den products-Grants.
    // Deshalb VOR dem Token-Entwerten durchs Speicher-Tor: bei Fehlschlag
    // wird NICHT abgemeldet (der saveFailed-Toast erklaert warum, der
    // Nutzer kann erneut abmelden oder die Korrektur ansehen). Ausserhalb
    // der Werkbank ist kein Waechter registriert und nichts aendert sich.
    if (!(await flushPendingEditsBeforeSelectionChange())) return;
    try {
      // K29: nur DIESES Geraet abmelden. Der Default `global` beendete jede
      // Sitzung des Nutzers auf allen Geraeten, ohne dass er das wollte.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.error('Logout error:', error);
      Sentry.captureException(error, { tags: { feature: 'auth', action: 'sign-out' } });
    } finally {
      // Clear ALL cached data to prevent cross-user leaks on next login
      queryClient.clear();
      clearPlanCache();
      clearCheckoutAwaiting();

      // Clear app-specific localStorage so next login starts fresh
      localStorage.removeItem('feed-optimizer-selection');
      localStorage.removeItem('feed-optimizer-onboarding-dismissed');
      localStorage.removeItem('feed-optimizer-onboarding-seen');
      localStorage.removeItem('feed-optimizer-onboarding-expanded');
      localStorage.removeItem('feed-optimizer-flow');

      // Always clear local state regardless of server response
      setSession(null);
      setUser(null);
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};